import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const LEAD_TYPE_LABELS = {
  contact_form: 'Contact', buy_form: 'Buyer', sell_form: 'Seller',
  tour_request: 'Tour', booking: 'Booking',
  property_gate: 'Property Gate', home_valuation: 'Home Valuation',
};

const METHOD_BADGE = {
  manual: { bg: '#EEF2FF', fg: '#4338CA' },
  rule: { bg: '#DCFCE7', fg: '#166534' },
  round_robin: { bg: '#FEF3C7', fg: '#92400E' },
};

const LeadRoutingAdmin = () => {
  const [agents, setAgents] = useState([]);
  const [rules, setRules] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showNewRule, setShowNewRule] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', keyword: '', source: '', agent_id: '', priority: 0 });
  const [assignPicker, setAssignPicker] = useState({});

  const refresh = async () => {
    try {
      const [ag, ru, un, as] = await Promise.all([
        api.get('/admin/lead-routing/agents'),
        api.get('/admin/lead-routing/rules'),
        api.get('/admin/lead-routing/unassigned'),
        api.get('/admin/lead-routing/assignments'),
      ]);
      setAgents(ag.data.agents);
      setRules(ru.data.rules);
      setUnassigned(un.data.unassigned);
      setAssignments(as.data.assignments);
    } catch (e) {
      toast.error('Failed to load routing data');
    }
  };
  useEffect(() => { refresh(); }, []);

  const createRule = async () => {
    if (!newRule.name || !newRule.agent_id) return toast.error('Name and agent are required');
    if (!newRule.keyword && !newRule.source) return toast.error('Set at least a keyword or source');
    const criteria = {};
    if (newRule.keyword) criteria.keyword = newRule.keyword;
    if (newRule.source) criteria.source = newRule.source;
    try {
      await api.post('/admin/lead-routing/rules', {
        name: newRule.name, criteria, agent_id: newRule.agent_id, priority: Number(newRule.priority) || 0,
      });
      toast.success('Rule created');
      setShowNewRule(false);
      setNewRule({ name: '', keyword: '', source: '', agent_id: '', priority: 0 });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create rule'); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await api.delete(`/admin/lead-routing/rules/${id}`); toast.success('Deleted'); refresh(); }
    catch (e) { toast.error('Failed to delete'); }
  };
  const manualAssign = async (leadType, leadId) => {
    const agentId = assignPicker[`${leadType}:${leadId}`];
    if (!agentId) return toast.error('Pick an agent first');
    try {
      await api.post('/admin/lead-routing/assign', { lead_id: leadId, lead_type: leadType, agent_id: agentId });
      toast.success('Lead assigned + notification sent');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assignment failed'); }
  };

  return (
    <div className="space-y-6" data-testid="lead-routing-admin">
      {/* Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Auto-Routing Rules</CardTitle>
          <Button size="sm" onClick={() => setShowNewRule(!showNewRule)} className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="new-rule-btn">
            <Plus className="h-4 w-4 mr-1" /> New Rule
          </Button>
        </CardHeader>
        <CardContent>
          {showNewRule && (
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Rule name</Label>
                <Input value={newRule.name} onChange={e => setNewRule({...newRule, name: e.target.value})} placeholder="e.g. Southaven → Yolanda" data-testid="rule-name" />
              </div>
              <div>
                <Label className="text-xs">Assign to agent</Label>
                <Select value={newRule.agent_id} onValueChange={v => setNewRule({...newRule, agent_id: v})}>
                  <SelectTrigger data-testid="rule-agent"><SelectValue placeholder="Select agent…" /></SelectTrigger>
                  <SelectContent>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name || a.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Keyword match (any field)</Label>
                <Input value={newRule.keyword} onChange={e => setNewRule({...newRule, keyword: e.target.value})} placeholder="e.g. Southaven, luxury, 38654" data-testid="rule-keyword" />
              </div>
              <div>
                <Label className="text-xs">Source (optional)</Label>
                <Select value={newRule.source} onValueChange={v => setNewRule({...newRule, source: v})}>
                  <SelectTrigger data-testid="rule-source"><SelectValue placeholder="Any source" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEAD_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority (higher wins)</Label>
                <Input type="number" value={newRule.priority} onChange={e => setNewRule({...newRule, priority: e.target.value})} data-testid="rule-priority" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button onClick={createRule} className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="save-rule">Save Rule</Button>
                <Button variant="outline" onClick={() => setShowNewRule(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {rules.length === 0 ? (
            <p className="text-sm text-gray-500">No rules yet — new leads auto-distribute via round-robin.</p>
          ) : (
            <div className="space-y-2">
              {rules.map(r => {
                const agent = agents.find(a => a.id === r.agent_id);
                return (
                  <div key={r.id} className="flex items-center justify-between p-3 border border-gray-100 rounded hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {r.criteria.keyword && <>keyword: <code className="bg-gray-100 px-1">{r.criteria.keyword}</code> · </>}
                        {r.criteria.source && <>source: <code className="bg-gray-100 px-1">{r.criteria.source}</code> · </>}
                        priority: {r.priority}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                      <span className="font-medium">{agent?.full_name || agent?.email || 'Unknown agent'}</span>
                      <Button size="sm" variant="ghost" onClick={() => deleteRule(r.id)} data-testid={`delete-rule-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned leads */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unassigned Leads ({unassigned.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {unassigned.length === 0 ? (
            <p className="text-sm text-gray-500">No unassigned leads.</p>
          ) : (
            <div className="space-y-2">
              {unassigned.slice(0, 20).map((row, i) => {
                const key = `${row.lead_type}:${row.lead.id}`;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 border border-gray-100 rounded" data-testid={`unassigned-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{row.lead.name || row.lead.email}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {LEAD_TYPE_LABELS[row.lead_type]} · {row.lead.email} · {row.lead.location_preference || row.lead.property_address || row.lead.address || ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={assignPicker[key] || ''}
                        onValueChange={v => setAssignPicker({ ...assignPicker, [key]: v })}
                      >
                        <SelectTrigger className="w-40" data-testid={`assign-picker-${i}`}><SelectValue placeholder="Agent…" /></SelectTrigger>
                        <SelectContent>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name || a.email}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => manualAssign(row.lead_type, row.lead.id)} className="bg-[#BCA480] hover:bg-[#a89470]" data-testid={`assign-btn-${i}`}>
                        Assign
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent assignments log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <p className="text-sm text-gray-500">No assignments yet.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {assignments.slice(0, 30).map((a, i) => {
                const badge = METHOD_BADGE[a.method] || METHOD_BADGE.round_robin;
                return (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{LEAD_TYPE_LABELS[a.lead_type] || a.lead_type}</span>
                      <span className="text-gray-500 mx-2">→</span>
                      <span>{a.agent?.full_name || a.agent?.email || a.agent_id}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded"
                        style={{ backgroundColor: badge.bg, color: badge.fg }}
                      >
                        {a.method}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(a.assigned_at).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadRoutingAdmin;
